import React, { useEffect } from "react";
import "./modal-base.css";
import { Dialog, DialogContent, IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { withoutBackdropClose } from "../../utils/dialogClose";
import { useWiserTheme } from "../../hooks/useWiserTheme";
import { getCtaButtonSx } from "../../shared/theme/wiserThemeHelpers";

interface ModalBaseProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  minWidth?: number | string;
  showCloseIcon?: boolean;
  sx?: object;
  contentSx?: object;
  disableScrollLock?: boolean;
}

const ModalBase: React.FC<ModalBaseProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "sm",
  minWidth = 400,
  showCloseIcon = true,
  sx = {},
  contentSx = {},
  disableScrollLock = false,
}) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const headerBg = darkModeActive ? tokens.surfaceMuted : "#F8FAFC";
  const bodyBg = darkModeActive ? tokens.surface : "#fff";
  const borderColor = darkModeActive ? tokens.border : "#E2E8F0";
  const titleColor = darkModeActive ? tokens.textPrimary : "#253858";
  const closeColor = darkModeActive ? tokens.textPrimary : "#1E293B";
  useEffect(() => {
    // Eğer disableScrollLock true ise scroll lock işlemi yapma
    if (disableScrollLock) return;

    if (open) {
      // paddingRight eklenmiyor: scrollbar gizlenince olusan layout kaymasi artik
      // index.css'teki `html { scrollbar-gutter: stable }` ile onleniyor. Genislik
      // hesaplayip padding vermek, gutter zaten ayrilmisken cift kaymaya yol aciyordu.
      const originalOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }
  }, [open, disableScrollLock]);

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      maxWidth={maxWidth}
      fullWidth
      disableScrollLock={true}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth,
          p: 0,
          backgroundColor: bodyBg,
          color: titleColor,
          ...sx,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "52px",
          background: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          px: 3,
          py: 1,
          position: "relative",
          flexShrink: 0,
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            p: 0,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: 0,
            color: titleColor,
            "& *": {
              color: "inherit",
            },
          }}
        >
          {title}
        </Box>
        {showCloseIcon && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              width: 24,
              height: 24,
              ml: 2,
              color: closeColor,
              position: "absolute",
              right: 24,
              top: "14px",
              p: 0,
              "&:hover": {
                background: darkModeActive
                  ? tokens.hoverBg
                  : "rgba(30,41,59,0.08)",
              },
            }}
          >
            <CloseIcon sx={{ width: 24, height: 24 }} />
          </IconButton>
        )}
      </Box>
      <DialogContent
        sx={{
          px: 3,
          py: 2,
          background: bodyBg,
          color: titleColor,
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>
      {actions && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            minHeight: "52px",
            background: headerBg,
            borderTop: `1px solid ${borderColor}`,
            px: 3,
            py: 1,
            gap: "12px",
            flexShrink: 0,
            "& .MuiButton-outlined": {
              borderColor: darkModeActive ? tokens.borderStrong : undefined,
              color: darkModeActive ? tokens.textPrimary : undefined,
              "&:hover": {
                borderColor: darkModeActive ? tokens.textPrimary : undefined,
                backgroundColor: darkModeActive ? tokens.hoverBg : undefined,
              },
            },
            ...(darkModeActive
              ? {
                  "& .MuiButton-contained:not(.MuiButton-containedError)": {
                    ...getCtaButtonSx(true),
                  },
                }
              : {}),
          }}
        >
          {actions}
        </Box>
      )}
    </Dialog>
  );
};

export default ModalBase;
